import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import CustomButton from '../../../components/CustomButton';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import SegmentedTabs from '../../../components/SegmentedTabs';
import ImportItemsTable from '../../../components/importReport/ImportItemsTable';
import OpenPointsList from '../../../components/importReport/OpenPointsList';
import { openPointsOf } from '../../../components/importReport/openPoints';
import {
    IMPORT_GROUPS,
    countByAction,
    countSkipped,
    downloadText,
    groupItems,
    isTextFileName,
    isUnanswered,
    promptWithSchema,
    shapeProblemText,
} from '../../../components/importReport/importReportView';
import {
    useApplyOrganizationImportMutation,
    useGetOrganizationImportFormatQuery,
    usePreviewOrganizationImportMutation,
    useSendOrganizationImportInvitesMutation,
} from '../../../api/organization/organizationApiSlice';
import type { OnboardingImportItem } from '../../../api/account/accountApiTypes';
import { CUSTOMER_ORGANIZATION_TYPES, CustomerOrganizationType } from '../../../api/organization/organizationApiTypes';
import type {
    OrganizationImportArgs,
    OrganizationImportReport,
    OrganizationImportResult,
} from '../../../api/organization/organizationImportTypes';
import { tenantSelector } from '../../../selector/authenticationSelector';
import { makeOrganizationDetailUrl, paths } from '../../../routes';
import { readTextFile } from '../../../utils/textFile';
import { ORGANIZATION_TYPE_LABEL_KEY } from '../organizationTypeLabels';
import { ORG_DETAIL_TAB_PARAM } from '../organizationDetailTabs';
import {
    ImportSource,
    InviteState,
    ORGANIZATION_FILE_ACCEPT,
    ORGANIZATION_IMPORT_GROUPS,
    PreviewBase,
    applyArgsOf,
    csvPoints,
    editedArgs,
    formatOf,
    hasTypeRow,
    importErrorText,
    invitesToSend,
    isOrganizationType,
    needsOwner,
    sendInviteBatches,
    typeOf,
    typeSourceOf,
    withUploadSheet,
} from './organizationImportView';

const card = 'bg-text-contrast rounded-[5px] shadow-sm px-5 py-4 flex flex-col gap-3';
const headerCell = 'text-left py-2 pr-3 text-foreground/60 font-medium';
const selectClass = 'rounded-md border border-foreground/15 px-2 py-1.5 text-sm bg-text-contrast text-foreground '
    + 'focus:outline-none focus:border-foreground/40';

interface FileState {
    name: string;
    text: string;
    format: ImportSource;
}

/**
 * "Organisationen importieren" (plan docs/plans/organization-import.md §5.3,
 * ADR-0003 sub-amendment 2026-11-01): a CSV export or a kolula file brings a
 * tenant's customer organisations with their units, contact persons, room
 * contracts, rooms and fixed weekly times. Source → upload → preview (a type
 * per organisation, changeable) → Übernehmen → result: invitations to send,
 * the rooms to decide on, the plan import. Nothing is kept between visits.
 */
const OrganizationImportContent: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const fileInput = useRef<HTMLInputElement>(null);
    const format = useGetOrganizationImportFormatQuery();
    const [source, setSource] = useState<ImportSource>('csv');
    const [file, setFile] = useState<FileState | null>(null);
    const [defaultType, setDefaultType] = useState<CustomerOrganizationType | ''>('');
    const [overrides, setOverrides] = useState<Record<string, CustomerOrganizationType>>({});
    const [base, setBase] = useState<PreviewBase | null>(null);
    const [report, setReport] = useState<OrganizationImportReport | null>(null);
    const [result, setResult] = useState<OrganizationImportResult | null>(null);
    const [resultCsv, setResultCsv] = useState<OrganizationImportReport['csv']>(undefined);
    const [error, setError] = useState<string | null>(null);
    const [unanswered, setUnanswered] = useState(false);
    const [copied, setCopied] = useState(false);
    const [invites, setInvites] = useState<Record<string, InviteState>>({});
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [preview, { isLoading: previewing }] = usePreviewOrganizationImportMutation();
    const [apply, { isLoading: applying }] = useApplyOrganizationImportMutation();
    const [sendInvites, { isLoading: sending }] = useSendOrganizationImportInvitesMutation();

    /**
     * Every check, file choice and source switch takes a new number; an answer
     * that arrives after a newer one was asked for is dropped, so the screen and
     * Übernehmen always belong to the file and choices the tenant sees.
     */
    const requestId = useRef(0);
    const nextRequest = () => {
        requestId.current += 1;
        return requestId.current;
    };
    const isCurrent = (id: number) => id === requestId.current;

    /** The upload as the server reads it, with the page default for a sheet's rows without a type. */
    const uploadArgs = (current: FileState, nextDefault: CustomerOrganizationType | ''): OrganizationImportArgs => ({
        fileText: current.text,
        format: current.format,
        ...(current.format === 'csv' && nextDefault ? { defaultType: nextDefault } : {}),
    });

    const refusal = (err: unknown) => {
        // Not the format at all: the refusal carries the report saying why.
        const details = (err as { data?: { details?: OrganizationImportReport } } | undefined)?.data?.details;
        setReport(details ?? null);
        setError(importErrorText(err, t));
    };

    /**
     * The edited file checked, the upload's preview kept as the base. If only
     * this check fails, the upload's preview stays usable: the error is shown,
     * the choice is rolled back to `fallback`, and what is on screen is the last
     * answer that belongs to it (`fallbackReport`).
     */
    const previewEdited = async (
        id: number,
        current: PreviewBase,
        nextOverrides: Record<string, CustomerOrganizationType>,
        fallback: Record<string, CustomerOrganizationType>,
        fallbackReport: OrganizationImportReport,
    ) => {
        if (!current.report.file) return;
        try {
            const edited = await preview(editedArgs(current.report.file, nextOverrides)).unwrap();
            if (!isCurrent(id)) return;
            setReport(withUploadSheet(edited, current.report));
        } catch (err) {
            if (!isCurrent(id)) return;
            setOverrides(fallback);
            setReport(fallbackReport);
            setError(importErrorText(err, t, true));
        }
    };

    /** Checks the upload; with types chosen here, then the edited file, which is what the preview then shows. */
    const runPreview = async (current: FileState, nextDefault: CustomerOrganizationType | '', nextOverrides: Record<string, CustomerOrganizationType>) => {
        const id = nextRequest();
        setError(null);
        const args = uploadArgs(current, nextDefault);
        let answer: OrganizationImportReport;
        try {
            answer = await preview(args).unwrap();
        } catch (err) {
            if (!isCurrent(id)) return;
            setBase(null);
            refusal(err);
            return;
        }
        if (!isCurrent(id)) return;
        const nextBase = { args, report: answer };
        setBase(nextBase);
        setReport(answer);
        if (Object.keys(nextOverrides).length > 0 && answer.file) await previewEdited(id, nextBase, nextOverrides, {}, answer);
    };

    /** Clears everything checked so far; nothing asked before stays allowed to answer. */
    const resetUpload = () => {
        nextRequest();
        setFile(null);
        setReport(null);
        setBase(null);
        setOverrides({});
        setError(null);
        setUnanswered(false);
        if (fileInput.current) fileInput.current.value = '';
    };

    const chooseFile = async (chosen: File | undefined) => {
        const id = nextRequest();
        setError(null);
        setResult(null);
        setReport(null);
        setBase(null);
        setOverrides({});
        setUnanswered(false);
        setFile(null);
        if (!chosen) return;
        try {
            // UTF-8, else Windows-1252 — what German Excel writes.
            const text = await readTextFile(chosen);
            if (!isCurrent(id)) return;
            const next = { name: chosen.name, text, format: formatOf(chosen.name, source) };
            setFile(next);
            await runPreview(next, defaultType, {});
        } catch {
            if (!isCurrent(id)) return;
            setError(t('organization_import.error.unreadable'));
        }
    };

    /** Another source: a file chosen for the other one is read anew, so nothing checked under it stays on screen. */
    const changeSource = (next: ImportSource) => {
        if (previewing || applying) return;
        setSource(next);
        if (file || report || error || unanswered) resetUpload();
    };

    const changeDefault = (value: string) => {
        const next = isOrganizationType(value) ? value : '';
        setDefaultType(next);
        if (file?.format === 'csv') runPreview(file, next, overrides);
    };

    /** A type chosen here: the upload's preview stays the base; only the edited file is checked (§5.1). */
    const changeType = (legalName: string, value: string) => {
        if (!base || !report || !isOrganizationType(value)) return;
        const id = nextRequest();
        setError(null);
        const previous = overrides;
        const next = { ...overrides, [legalName]: value };
        setOverrides(next);
        previewEdited(id, base, next, previous, report);
    };

    const runApply = async () => {
        const args = applyArgsOf(base, overrides);
        if (!args || !file) return;
        const edited = args !== base?.args;
        setError(null);
        setUnanswered(false);
        try {
            const written = await apply(args).unwrap();
            setResult(written);
            setResultCsv(base?.report.csv);
            setInvites(Object.fromEntries(written.invites.map((invite) => [invite.inviteId, 'pending' as InviteState])));
            setInviteError(null);
            resetUpload();
        } catch (err) {
            const details = (err as { data?: { details?: OrganizationImportReport } } | undefined)?.data?.details;
            if (details) setReport(edited && base ? withUploadSheet(details, base.report) : details);
            // No answer: the import may have committed. Re-checking shows it as "Unverändert"; nothing is written twice.
            if (isUnanswered(err)) setUnanswered(true);
            else setError(importErrorText(err, t, edited));
        }
    };

    const runSendInvites = async () => {
        if (!result) return;
        const ids = invitesToSend(result.invites, invites);
        if (ids.length === 0) return;
        setInviteError(null);
        const failure = await sendInviteBatches(ids, (batch) => sendInvites({ inviteIds: batch }).unwrap(), setInvites);
        if (!failure) return;
        // No answer: the call may have sent some of its mails before it was cut off.
        setInviteError(failure.unanswered ? t('organization_import.invites.unanswered') : importErrorText(failure.error, t));
    };

    const formatData = format.data;
    const copyPrompt = async () => {
        if (!formatData) return;
        try {
            await navigator.clipboard.writeText(promptWithSchema(formatData));
            setCopied(true);
        } catch {
            setError(t('organization_import.error.copy'));
        }
    };

    const readable = !!report && report.shapeProblems.length === 0;
    const busy = previewing || applying || sending;
    const canApply = readable && !!base && !!file && !unanswered && !previewing;
    const groups = report ? groupItems(report.items, ORGANIZATION_IMPORT_GROUPS) : [];
    const typeOptions = CUSTOMER_ORGANIZATION_TYPES;

    const renderType = (item: OnboardingImportItem) => {
        if (!report || !hasTypeRow(report.file, item)) return <span className={'text-foreground/40'}>{t('common.not_applicable')}</span>;
        const type = typeOf(report.file, overrides, item.key);
        const typeSource = typeSourceOf(item, report.file, overrides, report.csv);
        return (
            <div className={'flex flex-col gap-1'}>
                <select
                    aria-label={t('organization_import.type_of', { name: item.key })}
                    className={selectClass}
                    value={type ?? ''}
                    disabled={busy}
                    onChange={(event) => changeType(item.key, event.target.value)}
                >
                    {!type && <option value={''}>{t('common.missing')}</option>}
                    {typeOptions.map((option) => (
                        <option key={option} value={option}>{t(ORGANIZATION_TYPE_LABEL_KEY[option])}</option>
                    ))}
                </select>
                <span className={'text-xs text-foreground/60'}>
                    {t(`organization_import.type_source.${typeSource ?? 'none'}`)}
                </span>
            </div>
        );
    };

    const sourceTabs = [
        { key: 'csv' as const, label: t('organization_import.source.csv') },
        { key: 'json' as const, label: t('organization_import.source.json') },
    ];

    const resultPoints = result ? openPointsOf(result, null, t, i18n.language, 'organizations', csvPoints(resultCsv, t)) : [];
    const inviteCount = result?.invites.length ?? 0;
    const unsent = result ? invitesToSend(result.invites, invites).length : 0;

    return (
        <div className={'p-6 flex flex-col gap-5'}>
            <PageHeader>
                <div className={'flex-grow'}>
                    <PageHeadline>{t('organization_import.title')}</PageHeadline>
                    <p className={'text-sm text-foreground/60'}>{t('organization_import.subtitle')}</p>
                </div>
                <CustomButton title={t('organization_import.back')} isFilled={false} onClick={() => navigate(paths.customers)} />
            </PageHeader>

            <section className={card}>
                <h2 className={'text-base font-semibold text-foreground'}>{t('organization_import.source_title')}</h2>
                {/* No switch while a check or the apply runs: its answer belongs to the file on screen. */}
                <fieldset disabled={busy} className={'m-0 min-w-0 border-0 p-0'}>
                    <SegmentedTabs items={sourceTabs} value={source} onChange={changeSource} />
                </fieldset>
                {format.isLoading && <p className={'text-sm text-foreground/60'}>{t('organization_import.format_loading')}</p>}
                {format.isError && <p role={'alert'} className={'text-sm text-foreground'}>{t('organization_import.error.format')}</p>}
                {source === 'csv' && (
                    <>
                        <p className={'text-sm text-foreground/80'}>{t('organization_import.csv_hint')}</p>
                        {formatData && (
                            <div>
                                <CustomButton
                                    title={t('organization_import.template_download')}
                                    isFilled={false}
                                    // A byte-order mark, so Excel opens the umlauts of the headers as UTF-8.
                                    onClick={() => downloadText(`﻿${formatData.csvTemplate}`, t('organization_import.template_file'), 'text/csv')}
                                />
                            </div>
                        )}
                    </>
                )}
                {source === 'json' && (
                    <>
                        <p className={'text-sm text-foreground/80'}>{t('organization_import.json_hint')}</p>
                        <p className={'text-sm text-foreground/80'}>{t('organization_import.gdpr_note')}</p>
                        {formatData && (
                            <>
                                <pre className={'max-h-48 overflow-auto whitespace-pre-wrap rounded-[5px] bg-foreground/5 p-3 text-xs text-foreground'}>
                                    {formatData.prompt}
                                </pre>
                                <div className={'flex flex-wrap gap-3'}>
                                    <CustomButton
                                        title={t(copied ? 'organization_import.prompt_copied' : 'organization_import.prompt_copy')}
                                        onClick={copyPrompt}
                                    />
                                    <CustomButton
                                        title={t('organization_import.prompt_download')}
                                        isFilled={false}
                                        onClick={() => downloadText(promptWithSchema(formatData), t('organization_import.prompt_file'), 'text/plain')}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
            </section>

            <section className={card}>
                <h2 className={'text-base font-semibold text-foreground'}>{t('organization_import.upload_title')}</h2>
                <p className={'text-sm text-foreground/80'}>{t('organization_import.upload_hint')}</p>
                {/* Only a sheet has rows without a type; a kolula file states its types. */}
                {(source === 'csv' || file?.format === 'csv') && (
                    <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                        {t('organization_import.default_type')}
                        <select
                            className={`${selectClass} max-w-xs`}
                            value={defaultType}
                            disabled={busy}
                            onChange={(event) => changeDefault(event.target.value)}
                        >
                            <option value={''}>{t('organization_import.default_type_none')}</option>
                            {typeOptions.map((option) => (
                                <option key={option} value={option}>{t(ORGANIZATION_TYPE_LABEL_KEY[option])}</option>
                            ))}
                        </select>
                        <span>{t('organization_import.default_type_hint')}</span>
                    </label>
                )}
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('organization_import.file')}
                    <input
                        ref={fileInput}
                        type={'file'}
                        accept={ORGANIZATION_FILE_ACCEPT}
                        disabled={busy}
                        className={'text-sm text-foreground'}
                        onChange={(event) => { chooseFile(event.target.files?.[0]); }}
                    />
                </label>
                {file && (
                    <p className={'text-sm text-foreground/80'}>
                        {t('organization_import.file_chosen', {
                            name: file.name,
                            format: t(`organization_import.format_read.${file.format}`),
                        })}
                        {isTextFileName(file.name) && ` ${t('organization_import.file_txt_ok')}`}
                    </p>
                )}
                {previewing && <p className={'text-sm text-foreground/60'}>{t('organization_import.checking')}</p>}
                {error && <p role={'alert'} className={'text-sm text-foreground'}>{error}</p>}
            </section>

            {unanswered && (
                <section className={card} role={'alert'}>
                    <p className={'text-sm text-foreground'}>{t('organization_import.unanswered')}</p>
                    <div>
                        <CustomButton
                            title={t('organization_import.check_again')}
                            loading={previewing}
                            onClick={() => {
                                setUnanswered(false);
                                if (file) runPreview(file, defaultType, overrides);
                            }}
                        />
                    </div>
                </section>
            )}

            {report && report.shapeProblems.length > 0 && (
                <section className={card} role={'alert'}>
                    <h2 className={'text-base font-semibold text-foreground'}>{t('organization_import.not_file_title')}</h2>
                    <p className={'text-sm text-foreground/80'}>{t('organization_import.shape_hint')}</p>
                    <ul className={'list-disc pl-5 text-sm text-foreground/80'}>
                        {report.shapeProblems.map((p) => (
                            <li key={`${p.path}-${p.code}`}>{shapeProblemText(p, t)}</li>
                        ))}
                    </ul>
                </section>
            )}

            {report && readable && (
                <>
                    <p className={'text-sm text-foreground'}>
                        {t('organization_import.summary', { skipped: countSkipped(report.items), warnings: report.warnings })}
                    </p>
                    {needsOwner(report) && (
                        <section className={card} role={'note'}>
                            <p className={'text-sm text-foreground'}>{t('organization_import.owner_required')}</p>
                        </section>
                    )}
                    {groups.length === 0 && (
                        <section className={card}>
                            <p className={'text-sm text-foreground/60'}>{t('organization_import.empty')}</p>
                        </section>
                    )}
                    {groups.map(({ group, items }) => (
                        <ImportItemsTable
                            key={group}
                            title={t(`onboarding_import.group.${group}`)}
                            items={items}
                            {...(group === 'organization'
                                ? { extraColumn: { header: t('organization_import.column_type'), render: renderType } }
                                : {})}
                        />
                    ))}
                    <OpenPointsList
                        points={openPointsOf(report, null, t, i18n.language, 'organizations', csvPoints(report.csv, t))}
                        hint={t('organization_import.points.hint_preview')}
                        textTitle={t('organization_import.points.text_title')}
                    />
                    <div className={'flex flex-wrap gap-3'}>
                        <CustomButton title={t('organization_import.apply')} loading={applying} disabled={!canApply} onClick={runApply} />
                    </div>
                </>
            )}

            {result && (
                <>
                    <section className={card}>
                        <h2 className={'text-base font-semibold text-foreground'}>{t('organization_import.result_title')}</h2>
                        <ul className={'list-disc pl-5 text-sm text-foreground/80'}>
                            {IMPORT_GROUPS.map((group) => {
                                const counts = countByAction(result.items, group);
                                if (counts.create + counts.update === 0) return null;
                                return (
                                    <li key={group}>
                                        {t('organization_import.result_line', {
                                            group: t(`onboarding_import.group.${group}`), created: counts.create, updated: counts.update,
                                        })}
                                    </li>
                                );
                            })}
                        </ul>
                        {countSkipped(result.items) > 0 && (
                            <p className={'text-sm font-medium text-foreground'}>
                                {t('organization_import.result_skipped', { count: countSkipped(result.items) })}
                            </p>
                        )}
                        <p className={'text-sm text-foreground/80'}>{t('organization_import.type_later')}</p>
                    </section>

                    <OpenPointsList
                        points={resultPoints}
                        hint={t('organization_import.points.hint_result')}
                        textTitle={t('organization_import.points.text_title')}
                    />

                    <section className={card}>
                        <h2 className={'text-base font-semibold text-foreground'}>{t('organization_import.invites.title')}</h2>
                        {inviteCount === 0
                            ? <p className={'text-sm text-foreground/60'}>{t('organization_import.invites.none')}</p>
                            : (
                                <>
                                    <p className={'text-sm text-foreground/80'}>{t('organization_import.invites.hint')}</p>
                                    <div className={'overflow-x-auto'}>
                                        <table className={'w-full text-sm'}>
                                            <thead>
                                                <tr className={'border-b border-foreground/10'}>
                                                    <th className={headerCell}>{t('organization_import.invites.email')}</th>
                                                    <th className={headerCell}>{t('organization_import.invites.organization')}</th>
                                                    <th className={headerCell}>{t('organization_import.invites.state_column')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.invites.map((invite) => (
                                                    <tr key={invite.inviteId} className={'border-b border-foreground/5 align-top'}>
                                                        <td className={'py-2 pr-3 text-foreground'}>{invite.email}</td>
                                                        <td className={'py-2 pr-3 text-foreground'}>{invite.organizationName}</td>
                                                        <td className={'py-2 text-foreground'}>
                                                            {t(`organization_import.invites.state.${invites[invite.inviteId] ?? 'pending'}`)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {inviteError && <p role={'alert'} className={'text-sm text-foreground'}>{inviteError}</p>}
                                    {unsent > 0 && (
                                        <div>
                                            <CustomButton
                                                title={t(unsent === inviteCount ? 'organization_import.invites.send' : 'organization_import.invites.send_rest')}
                                                loading={sending}
                                                onClick={runSendInvites}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                    </section>

                    <section className={card}>
                        <h2 className={'text-base font-semibold text-foreground'}>{t('organization_import.rooms.title')}</h2>
                        {result.rooms.length === 0
                            ? <p className={'text-sm text-foreground/60'}>{t('organization_import.rooms.none')}</p>
                            : (
                                <>
                                    <p className={'text-sm text-foreground/80'}>{t('organization_import.rooms.hint')}</p>
                                    <ul className={'flex flex-col gap-1 text-sm'}>
                                        {result.rooms.map((room) => (
                                            <li key={`${room.groupId}-${room.organizationId}`} className={'text-foreground'}>
                                                {t('organization_import.rooms.line', {
                                                    room: room.roomName ?? t('common.missing'), organization: room.organizationName,
                                                })}
                                                {' '}
                                                <Link
                                                    className={'text-primary hover:underline'}
                                                    to={`${makeOrganizationDetailUrl(room.organizationId)}?${ORG_DETAIL_TAB_PARAM}=rooms`}
                                                >
                                                    {t('organization_import.rooms.open')}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                    </section>

                    <section className={card}>
                        <h2 className={'text-base font-semibold text-foreground'}>{t('organization_import.plan.title')}</h2>
                        <p className={'text-sm text-foreground/80'}>{t('organization_import.plan.hint')}</p>
                        <div>
                            <Link className={'text-sm text-primary hover:underline'} to={`${paths.bookings}?view=allocations`}>
                                {t('organization_import.plan.open')}
                            </Link>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

/** The page: one content instance per tenant in view, so a switch starts clean (#909/#929). */
const OrganizationImportPage: React.FC = () => {
    const selectedTenant = useSelector(tenantSelector);
    return <OrganizationImportContent key={selectedTenant ?? ''} />;
};

export default OrganizationImportPage;
